/** Give up waiting for images and jump to the hash at this point. */
const MAX_WAIT_MS = 3000;

/**
 * Scrolls the element matching `window.location.hash` into view.
 *
 * @returns The target element, or `null` if missing / no hash.
 */
export function getHashTarget(): HTMLElement | null {
  if (typeof window === 'undefined' || !window.location.hash) {
    return null;
  }

  let targetId = window.location.hash.slice(1);
  try {
    targetId = decodeURIComponent(targetId);
  } catch {
    /* do nothing */
  }
  if (!targetId) {
    return null;
  }

  return document.getElementById(targetId);
}

/**
 * Instantly scrolls the hash target into view, bypassing CSS
 * `scroll-behavior: smooth` so first-load correction is not animated.
 *
 * @returns The target element, or `null` if missing / no hash.
 */
export function scrollToHashTarget(): HTMLElement | null {
  const targetElement = getHashTarget();
  if (!targetElement) {
    return null;
  }

  const root = document.documentElement;
  const previousBehavior = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  targetElement.scrollIntoView({ behavior: 'auto', block: 'start' });
  root.style.scrollBehavior = previousBehavior;
  return targetElement;
}

/**
 * Images that appear before `target` in document order (these shift its Y).
 */
function getImagesAbove(target: Element): HTMLImageElement[] {
  const root = document.querySelector<HTMLElement>('.content') ?? document.body;
  return Array.from(root.querySelectorAll('img')).filter(
    (img) =>
      !!(
        img.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING
      ),
  );
}

/**
 * Resolves when the image has loaded, failed, or is already complete.
 */
function waitForImage(img: HTMLImageElement): Promise<void> {
  if (img.complete) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const done = (): void => {
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      resolve();
    };
    img.addEventListener('load', done);
    img.addEventListener('error', done);
  });
}

/**
 * On first paint with a URL hash, wait for images above the target (they
 * change layout height) then scroll once. Avoids ResizeObserver chasing,
 * which feels laggy while many images settle.
 *
 * Stops if the user scrolls, or after {@link MAX_WAIT_MS}.
 *
 * @returns Cleanup function (safe to call multiple times).
 */
export function registerAnchorScroll(): () => void {
  if (typeof window === 'undefined' || !window.location.hash) {
    return () => undefined;
  }

  const targetElement = getHashTarget();
  if (!targetElement) {
    return () => undefined;
  }

  let stopped = false;
  let rafId = 0;
  const root = document.documentElement;
  const previousBehavior = root.style.scrollBehavior;
  // Native hash navigation uses CSS smooth scroll; freeze it during wait.
  root.style.scrollBehavior = 'auto';
  const timers: { wait?: ReturnType<typeof setTimeout> } = {};

  const restoreScrollBehavior = (): void => {
    root.style.scrollBehavior = previousBehavior;
  };

  const stop = (): void => {
    if (stopped) {
      return;
    }
    stopped = true;
    restoreScrollBehavior();
    if (timers.wait !== undefined) {
      clearTimeout(timers.wait);
    }
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
  };

  const applyScroll = (): void => {
    if (stopped) {
      return;
    }
    scrollToHashTarget();
    stop();
  };

  const scheduleScroll = (): void => {
    if (stopped) {
      return;
    }
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(applyScroll);
  };

  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });

  timers.wait = setTimeout(scheduleScroll, MAX_WAIT_MS);

  void Promise.all(getImagesAbove(targetElement).map(waitForImage)).then(
    scheduleScroll,
  );

  return stop;
}

/**
 * @deprecated Prefer {@link registerAnchorScroll}; kept for existing call sites.
 */
export const anchorScroll = registerAnchorScroll;
